import { type MouseEvent, useCallback } from 'react';
import { openChatPanel } from './chat-panel-state';
import { type CurrentPageChatBinding } from './current-page-chat-binding';

/**
 * Shared click handler for the "Ask AI" entry points (the public-database
 * top-bar link and the DMAv2-dashboard white-header link). Opening the
 * floating side panel rather than navigating to full-page `/chat` is the
 * behaviour every entry point wants; keeping it in one hook stops the call
 * sites from drifting apart (PLT-5396 — the DMA header used to plain-navigate
 * to `/chat` and so had no in-page sidebar mode at all).
 *
 * The hook owns only the behaviour; each call site keeps its own markup (the
 * public side renders a nav-styled `<a>`, the DMA side a white-header
 * `HeaderLink`) and passes the returned handler as its `onClick`.
 *
 * Click semantics, mirrored from the original public-side link:
 *   - Plain primary click → intercept (`preventDefault`) and open the panel.
 *     The panel always opens on a new conversation (PLT-7880). When the
 *     current page published an entity binding we pre-apply it plus a
 *     page-context chip so the chat opens about that entity; otherwise it
 *     opens on the plain welcome state.
 *   - Modifier / middle click (cmd / ctrl / shift / alt / non-primary button)
 *     → return early WITHOUT `preventDefault`, letting native navigation (and,
 *     for a React Router `Link`, its own modified-click fall-through) open
 *     full-page `/chat` in a new tab. The caller must still supply the
 *     `href` so this path has somewhere to go.
 */
export function useOpenAskAiPanel(binding: CurrentPageChatBinding | null): (e: MouseEvent<HTMLAnchorElement>) => void {
  return useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (binding !== null) {
        // Read the page href from `window.location` at click time rather than
        // at publish time: the binding is published by an outer view that
        // doesn't know which sub-route is currently mounted, so reading it now
        // pins the chat's hidden context line to the precise URL the user is on.
        const pageHref =
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}${window.location.hash}`
            : '';
        openChatPanel({
          entityId: binding.entityId,
          entityType: binding.entityType,
          // No `prompt` — "Ask AI" opens the composer for the user to type
          // their own question rather than auto-submitting a canned one.
          pageContext: {
            label: binding.label,
            href: pageHref,
          },
        });
      } else {
        openChatPanel({});
      }
    },
    [binding],
  );
}
